import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, X, ChevronDown, ChevronUp, Users, Rocket, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { Button } from '../components/ui/button';

const Solutions = () => {
  const { language } = useLanguage();
  const t = translations[language].solutions;
  const [openFaq, setOpenFaq] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);

  const faqs = [
    { q: t.faq.q1, a: t.faq.a1 },
    { q: t.faq.q2, a: t.faq.a2 },
    { q: t.faq.q3, a: t.faq.a3 },
    { q: t.faq.q4, a: t.faq.a4 },
    { q: t.faq.q5, a: t.faq.a5 },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="py-16 px-4" style={{ backgroundColor: '#6949b6' }}>
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            {t.hero.title}
          </h1>
          <p className="text-xl text-purple-200 mb-8">{t.hero.subtitle}</p>
          
          {/* Toggle Mensual/Anual */}
          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm font-medium ${!isAnnual ? 'text-white' : 'text-purple-300'}`}>
              {t.toggle.monthly}
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
                isAnnual ? 'bg-orange-400' : 'bg-purple-400'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-300 ${
                  isAnnual ? 'translate-x-7' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${isAnnual ? 'text-white' : 'text-purple-300'}`}>
              {t.toggle.annual}
            </span>
            {isAnnual && (
              <span className="ml-2 px-3 py-1 bg-green-400 text-green-900 text-xs font-semibold rounded-full">
                {t.toggle.save}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="pricing-plans" className="py-16 px-4 -mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Free Plan */}
            <Card className="border-2 border-dashed border-gray-300 hover:border-purple-200 transition-all duration-300 bg-gray-50/50">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {language === 'es' ? 'Gratis' : 'Free'}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold text-gray-700">USD 0</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {language === 'es' ? 'Prueba la plataforma' : 'Try the platform'}
                  </p>
                </div>

                <div className="space-y-3 mb-6">
                  {(language === 'es' ? [
                    '1 desafio publicado',
                    'Recibe postulaciones',
                    'Ver hasta 20 postulaciones completas',
                    '1 evaluador IA incluido',
                    'Dashboard basico',
                  ] : [
                    '1 published challenge',
                    'Receive applications',
                    'View up to 20 full applications',
                    '1 AI evaluator included',
                    'Basic dashboard',
                  ]).map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-4 mb-6">
                  <p className="text-xs text-gray-500 mb-2 font-medium">{language === 'es' ? 'No incluye:' : 'Not included:'}</p>
                  {(language === 'es' ? [
                    'Desafios ilimitados',
                    'Multiples evaluadores IA',
                    'Exportacion de datos',
                    'Soporte',
                  ] : [
                    'Unlimited challenges',
                    'Multiple AI evaluators',
                    'Data export',
                    'Support',
                  ]).map((item, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <X className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-500">{item}</span>
                    </div>
                  ))}
                </div>

                <Button
                  asChild
                  variant="outline"
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  <Link to="/register?plan=free">{language === 'es' ? 'Comenzar gratis' : 'Start for free'}</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Plan Starter */}
            <Card className="border-2 border-gray-200 hover:border-purple-300 transition-all duration-300 bg-white">
              <CardContent className="p-6 md:p-8">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t.plans.starter.name}</h3>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold" style={{ color: '#6949b6' }}>
                      USD {isAnnual ? t.plans.starter.annualPrice : t.plans.starter.monthlyPrice}
                    </span>
                    <span className="text-gray-500">/ {isAnnual ? t.period.year : t.period.month}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t.plans.starter.tagline}</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  {t.plans.starter.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-4 mb-6">
                  <p className="text-xs text-gray-500 mb-2 font-medium">{language === 'es' ? 'No incluye:' : 'Not included:'}</p>
                  {t.plans.starter.notIncluded.map((item, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <X className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-500">{item}</span>
                    </div>
                  ))}
                </div>

                <Button
                  asChild
                  className="w-full text-white"
                  style={{ backgroundColor: '#6949b6' }}
                >
                  <Link to={`/register?plan=starter&cycle=${isAnnual ? 'annual' : 'monthly'}`}>{t.plans.starter.cta}</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Plan Growth - Destacado */}
            <Card className="border-2 border-purple-400 shadow-xl relative bg-white">
              <div 
                className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-white text-sm font-semibold"
                style={{ backgroundColor: '#ff9400' }}
              >
                <Star className="w-4 h-4 inline mr-1" />
                {language === 'es' ? 'Más Popular' : 'Most Popular'}
              </div>
              <CardContent className="p-6 md:p-8">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t.plans.growth.name}</h3>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold" style={{ color: '#6949b6' }}>
                      USD {isAnnual ? t.plans.growth.annualPrice : t.plans.growth.monthlyPrice}
                    </span>
                    <span className="text-gray-500">/ {isAnnual ? t.period.year : t.period.month}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t.plans.growth.tagline}</p>
                </div>

                <p className="text-xs font-semibold text-purple-600 mb-3">{t.plans.growth.includesStarter}</p>
                
                <div className="space-y-3 mb-6">
                  {t.plans.growth.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-4 mb-4">
                  <p className="text-xs text-gray-500 mb-2 font-medium">{language === 'es' ? 'No incluye:' : 'Not included:'}</p>
                  {t.plans.growth.notIncluded.map((item, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <X className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-500">{item}</span>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-600 italic mb-6">{t.plans.growth.idealFor}</p>

                <Button
                  asChild
                  className="w-full text-white"
                  style={{ backgroundColor: '#ff9400' }}
                >
                  <Link to={`/register?plan=growth&cycle=${isAnnual ? 'annual' : 'monthly'}`}>{t.plans.growth.cta}</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Plan Pro */}
            <Card className="border-2 border-gray-200 hover:border-purple-300 transition-all duration-300 bg-gradient-to-b from-white to-purple-50">
              <CardContent className="p-6 md:p-8">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t.plans.pro.name}</h3>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold" style={{ color: '#6949b6' }}>
                      USD {isAnnual ? t.plans.pro.annualPrice : t.plans.pro.monthlyPrice}
                    </span>
                    <span className="text-gray-500">/ {isAnnual ? t.period.year : t.period.month}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t.plans.pro.tagline}</p>
                </div>

                <p className="text-xs font-semibold text-purple-600 mb-3">{t.plans.pro.includesGrowth}</p>
                
                <div className="space-y-3 mb-6">
                  {t.plans.pro.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-600 italic mb-6">{t.plans.pro.idealFor}</p>

                <Button
                  asChild
                  variant="outline"
                  className="w-full border-2"
                  style={{ borderColor: '#6949b6', color: '#6949b6' }}
                >
                  <a href="mailto:contacto@colaborachile.cl">{t.plans.pro.cta}</a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {t.addons.title}
            </h2>
            <p className="text-gray-600 text-lg">{t.addons.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.addons.items.map((addon, idx) => (
              <Card key={idx} className="border border-gray-200 hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <h4 className="font-semibold text-gray-900 mb-1">{addon.name}</h4>
                  <p className="text-lg font-bold mb-2" style={{ color: '#6949b6' }}>{addon.price}</p>
                  <p className="text-sm text-gray-600 mb-3">{addon.description}</p>
                  <p className="text-xs text-gray-500 italic border-t border-gray-100 pt-2">
                    {t.addons.availableNote}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* For Solvers */}
      <section className="py-16 px-4 bg-gradient-to-r from-teal-500 to-teal-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Users className="w-10 h-10" />
            <h2 className="text-2xl md:text-3xl font-bold">{t.solvers.title}</h2>
          </div>
          <p className="text-4xl font-bold mb-4">{t.solvers.price}</p>
          <p className="text-teal-100 text-lg mb-2">{t.solvers.description}</p>
          <p className="text-teal-100">{t.solvers.belief}</p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            ❓ {t.faq.title}
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <Card 
                key={idx} 
                className="border border-gray-200 cursor-pointer hover:border-purple-300 transition-all"
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
              >
                <CardContent className="p-0">
                  <div className="flex items-center justify-between p-5">
                    <h3 className="font-semibold text-gray-900 pr-4">{faq.q}</h3>
                    {openFaq === idx ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </div>
                  {openFaq === idx && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-gray-600 whitespace-pre-line">{faq.a}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4" style={{ backgroundColor: '#6949b6' }}>
        <div className="max-w-3xl mx-auto text-center text-white">
          <Rocket className="w-16 h-16 mx-auto mb-6 text-purple-200" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.finalCta.title}</h2>
          <p className="text-purple-200 text-lg mb-8">{t.finalCta.subtitle}</p>
          <a
            href="#pricing-plans"
            className="inline-block px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 text-purple-900"
            style={{ backgroundColor: '#ffffff' }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            {t.finalCta.cta}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
